import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clientesApi } from '@/lib/api'
import type { Client, ClientCreate } from '@/types'
import {
  Users,
  Plus,
  Trash2,
  PauseCircle,
  PlayCircle,
  X,
  Search,
  AlertTriangle,
  Euro,
} from 'lucide-react'
import { formatDateTime } from '@/lib/utils'
import { cn } from '@/lib/utils'

const EMPTY_FORM: ClientCreate = {
  name: '',
  email: '',
  phone: '',
  nif: '',
  notes: '',
  saldo: 0,
}

export default function ClientesPage() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<ClientCreate>(EMPTY_FORM)
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['clients', search],
    queryFn: () => clientesApi.list({ search: search || undefined }),
  })

  const createMutation = useMutation({
    mutationFn: clientesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      setShowForm(false)
      setForm(EMPTY_FORM)
      setErrorMsg('')
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErrorMsg(msg ?? 'Erro ao criar cliente.')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { is_active: boolean } }) =>
      clientesApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: clientesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      setDeleteConfirm(null)
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setDeleteConfirm(null)
      alert(msg ?? 'Não é possível apagar este cliente.')
    },
  })

  const clients: Client[] = data?.data?.items ?? []

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg('')
    createMutation.mutate({
      ...form,
      email: form.email || undefined,
      phone: form.phone || undefined,
      nif: form.nif || undefined,
      notes: form.notes || undefined,
    })
  }

  const hasSaldo = (c: Client) => c.saldo !== 0

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestão de clientes — {clients.length} registos
          </p>
        </div>
        <button
          onClick={() => { setShowForm(true); setErrorMsg('') }}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Novo Cliente
        </button>
      </div>

      {/* Search */}
      <div className="mt-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Pesquisar nome, email, NIF..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-9 w-full"
        />
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="mt-6 card border border-primary-200 bg-primary-50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Novo Cliente</h2>
            <button
              onClick={() => { setShowForm(false); setErrorMsg('') }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">NIF</label>
                <input
                  type="text"
                  value={form.nif}
                  onChange={(e) => setForm({ ...form, nif: e.target.value })}
                  className="input w-full"
                  placeholder="ex: 123456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Saldo inicial (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.saldo}
                  onChange={(e) => setForm({ ...form, saldo: parseFloat(e.target.value) || 0 })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>
            {errorMsg && (
              <p className="text-sm text-red-600 font-medium">{errorMsg}</p>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowForm(false); setErrorMsg('') }}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="btn btn-primary"
              >
                {createMutation.isPending ? 'A criar...' : 'Criar Cliente'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="mt-6 card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Users className="h-12 w-12 mb-3" />
            <p className="text-sm">Nenhum cliente encontrado.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>NIF</th>
                <th>Contacto</th>
                <th>Saldo</th>
                <th>Estado</th>
                <th>Criado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr
                  key={client.id}
                  className={cn(!client.is_active && 'opacity-50 bg-gray-50')}
                >
                  {/* Name */}
                  <td>
                    <div className="font-medium text-gray-900">{client.name}</div>
                    {client.notes && (
                      <div className="text-xs text-gray-400 truncate max-w-[180px]">
                        {client.notes}
                      </div>
                    )}
                  </td>

                  {/* NIF */}
                  <td className="text-sm text-gray-600">{client.nif ?? '—'}</td>

                  {/* Contact */}
                  <td>
                    <div className="text-sm text-gray-700">{client.email ?? ''}</div>
                    <div className="text-xs text-gray-400">{client.phone ?? ''}</div>
                  </td>

                  {/* Saldo */}
                  <td>
                    {hasSaldo(client) ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-sm font-semibold text-amber-800">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {client.saldo.toFixed(2)} €
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-sm font-semibold text-green-700">
                        <Euro className="h-3.5 w-3.5" />
                        0,00 €
                      </span>
                    )}
                  </td>

                  {/* Status */}
                  <td>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
                        client.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      )}
                    >
                      {client.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>

                  {/* Created at */}
                  <td className="text-sm text-gray-500">{formatDateTime(client.created_at)}</td>

                  {/* Actions */}
                  <td>
                    <div className="flex items-center gap-2">
                      {/* Toggle active */}
                      <button
                        onClick={() =>
                          updateMutation.mutate({ id: client.id, data: { is_active: !client.is_active } })
                        }
                        title={client.is_active ? 'Tornar inativo' : 'Ativar'}
                        className={cn(
                          'rounded p-1.5 transition-colors',
                          client.is_active
                            ? 'text-amber-500 hover:bg-amber-50'
                            : 'text-green-600 hover:bg-green-50'
                        )}
                      >
                        {client.is_active ? (
                          <PauseCircle className="h-4 w-4" />
                        ) : (
                          <PlayCircle className="h-4 w-4" />
                        )}
                      </button>

                      {/* Delete */}
                      {deleteConfirm === client.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => deleteMutation.mutate(client.id)}
                            className="rounded px-2 py-1 text-xs font-medium bg-red-600 text-white hover:bg-red-700"
                          >
                            Confirmar
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="rounded px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            if (hasSaldo(client)) {
                              alert(
                                `Não é possível apagar "${client.name}".\nTem saldo pendente de ${client.saldo.toFixed(2)} €.\nLiquide o saldo antes de apagar, ou torne-o inativo.`
                              )
                              return
                            }
                            setDeleteConfirm(client.id)
                          }}
                          title={
                            hasSaldo(client)
                              ? `Não pode apagar — saldo: ${client.saldo.toFixed(2)} €`
                              : 'Apagar cliente'
                          }
                          className={cn(
                            'rounded p-1.5 transition-colors',
                            hasSaldo(client)
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-red-500 hover:bg-red-50'
                          )}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
